import { useState, useEffect } from 'react'
import { Breadcrumbs, Button, Layout } from '@loadsmart/loadsmart-ui'
import { Paper, Divider, Box, Menu, MenuItem, IconButton } from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import SimplePagination from 'atoms/SimplePagination'
import useDebouncedValue from 'hooks/useDebouncedValue'
import { TranslationMap } from 'common/types/kraken-core/TranslationMap'
import { parse, stringfy } from 'common/helpers/queryString'
import { useSearchTranslationMaps, useDeleteTranslationMapSetting } from '../api'
import ImportModal from '../components/ImportModal'
import { useImportModalContext } from '../contexts/ImportModalContext'
import TranslationMapsListFilters from '../components/TranslationMapsListFilters'
import TranslationMapsListTable from '../components/TranslationMapsListTable'
import useTopNavigationContext from 'hooks/useTopNavigationContext/useTopNavigationContext'
import { useTabTitle } from 'hooks/useTabTitle/useTabTitle'
import ConfirmationDialog, {
  ConfirmationDialogProps,
} from 'molecules/ConfirmationDialog/ConfirmationDialog'

const TranslationMapsList = () => {
  useTabTitle('Translation Maps')

  const urlFilters = parse(window.location.search?.slice(1) ?? '')
  const [filters, setFilters] = useState<any>(() => {
    // Clean up any null/undefined values from URL filters
    const cleanFilters = { ...urlFilters }
    if (cleanFilters.loader === null || cleanFilters.loader === undefined) {
      delete cleanFilters.loader
    }
    return cleanFilters
  })
  const debouncedFilters = useDebouncedValue(filters, 500)

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
  })
  const { data, refetch, isLoading, isError } = useSearchTranslationMaps({
    ...debouncedFilters,
    ...pagination,
  })

  const { mutate: deleteTranslationMap, data: deletedData } = useDeleteTranslationMapSetting()

  const { openModal } = useImportModalContext()
  const [confirmationDialog, setConfirmationDialog] = useState<
    ConfirmationDialogProps | undefined
  >()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleNextPage = () => {
    if (pagination.page) {
      setPagination(c => ({ ...c, page: (c.page || 1) + 1 }))
    }
  }

  const handlePreviousPage = () => {
    if (pagination.page && pagination.page > 1) {
      setPagination(c => ({ ...c, page: (c.page || 1) - 1 }))
    }
  }

  const handleOnPage = (pageSize: number) => {
    setPagination(p => ({ ...p, page_size: pageSize, page: 1 }))
  }

  const handleOnCreateNew = () => {
    openModal('create')
  }

  const handleOnImport = () => {
    openModal('import')
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const topNavigationContext = useTopNavigationContext()

  useEffect(() => {
    topNavigationContext.updateState({
      children: (
        <Layout.Group className="w-full" align="center" justify="space-between">
          <Breadcrumbs
            entries={[
              {
                label: 'Translation Maps',
                active: true,
              },
            ]}
          />
          <Layout.Group>
            <Box>
              <IconButton onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem
                  onClick={() => {
                    handleOnImport()
                    handleMenuClose()
                  }}
                >
                  Import
                </MenuItem>
              </Menu>
            </Box>
            <Button variant="primary" onClick={handleOnCreateNew}>
              Create
            </Button>
          </Layout.Group>
        </Layout.Group>
      ),
    })
  }, [anchorEl])

  const showConfirmationDialog = (title: string, content: string, callback: () => void) => {
    setConfirmationDialog({
      title,
      content,
      onConfirm: callback,
      onClose: () => {
        toggleConfirmationDialog()
      },
      open: true,
    })
  }

  const toggleConfirmationDialog = () =>
    setConfirmationDialog(prev => (prev ? { ...prev, open: false } : undefined))

  const handleOnRemove = (entity: TranslationMap) => {
    showConfirmationDialog(
      'Delete Translation Map',
      'Are you sure you want to delete this translation map setting?',
      () => {
        deleteTranslationMap(entity.id)
        toggleConfirmationDialog()
      }
    )
  }

  useEffect(() => {
    window.history.replaceState(null, '', `?${stringfy(filters)}`)
  }, [filters])

  useEffect(() => {
    refetch()
  }, [deletedData])

  return (
    <>
      {confirmationDialog ? <ConfirmationDialog {...confirmationDialog} /> : null}
      <ImportModal onClose={() => {}} open={false} mode={null} />

      <Paper elevation={1} data-testid="translation-maps-list">
        <Layout.Stack>
          <Box p={2}>
            <TranslationMapsListFilters
              filters={filters}
              onChange={changed => {
                setFilters(changed)
                // Reset to page 1 when filters change
                if (pagination.page > 1) {
                  setPagination(prev => ({ ...prev, page: 1 }))
                }
              }}
            />
          </Box>

          <Divider />

          <TranslationMapsListTable
            data={data?.results ?? []}
            onRemove={handleOnRemove}
            isLoading={isLoading}
          />
          <Layout.Box padding="s">
            <SimplePagination
              currentPage={pagination.page ?? 0}
              nextPage={handleNextPage}
              previousPage={handlePreviousPage}
              pageSize={pagination.page_size}
              canPreviousPage={pagination.page > 1}
              canNextPage={Boolean(data?.next)}
              onPageSize={handleOnPage}
            />
          </Layout.Box>
        </Layout.Stack>
      </Paper>
    </>
  )
}

export default TranslationMapsList
